import React, { useCallback, useEffect, useState } from "react";
import { FiSlash, FiUser } from "react-icons/fi";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import { FleetAgent } from "../../../../interfaces/Equipament";
import { lastSeenLabel } from "../widgetVersion";

/**
 * Quem já usou este notebook — montado do que as máquinas reportaram, não do que alguém digitou.
 *
 * Cada linha é uma instalação do widget num perfil do Windows daquela máquina. É por isso que a
 * lista responde a pergunta sem depender de ninguém manter cadastro: o widget de cada perfil se
 * enrolou sozinho, e a credencial revogada continua aqui como registro do período.
 */
interface FleetAgentsPanelProps {
  equipamentId: number;
  /** Dono atual, para não oferecer "definir como dono" a quem já é. */
  ownerEmployeeId?: number | null;
  /** Posse fixada: muda a ação oferecida de "fixar" para "soltar". */
  ownerPinned?: boolean;
  /** Avisa a tela de trás que a posse mudou — senão a lista fica mostrando o dono velho. */
  onOwnerChanged?: () => void;
}

const FleetAgentsPanel: React.FC<FleetAgentsPanelProps> = ({
  equipamentId,
  ownerEmployeeId,
  ownerPinned,
  onOwnerChanged,
}) => {
  const [agents, setAgents] = useState<FleetAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [assigning, setAssigning] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<FleetAgent[]>(`/Fleet/equipament/${equipamentId}/agents`);
      setAgents(res.data ?? []);
    } catch {
      // Backend anterior à frota, ou sem permissão: a seção some em vez de mostrar erro para
      // quem não pode fazer nada a respeito.
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [equipamentId]);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (agent: FleetAgent) => {
    setRevoking(agent.id);
    try {
      await api.post(`/Fleet/agents/${agent.id}/revoke`, {
        reason: "Revogado pelo portal",
      });
      toast.success("Credencial revogada. A máquina para de reportar no próximo ciclo.");
      await load();
    } catch {
      toast.error("Não foi possível revogar a credencial.");
    } finally {
      setRevoking(null);
    }
  };

  /**
   * Fixa a posse nesta pessoa. `pinned: true` porque isto é decisão de gente: a regra de
   * predominância de uso (7 dias) para de mexer neste equipamento a partir daqui.
   */
  const assignOwner = async (agent: FleetAgent) => {
    setAssigning(agent.id);
    try {
      await api.post(`/Equipament/${equipamentId}/owner`, {
        employeeId: agent.employeeId,
        pinned: true,
      });
      toast.success(`Equipamento atribuído a ${agent.employeeName}.`);
      onOwnerChanged?.();
    } catch {
      toast.error("Não foi possível definir o dono.");
    } finally {
      setAssigning(null);
    }
  };

  /** Solta a posse: a máquina volta a seguir o uso real, sem perder o dono atual. */
  const unpinOwner = async () => {
    setAssigning(-1);
    try {
      await api.post(`/Equipament/${equipamentId}/owner`, {
        employeeId: ownerEmployeeId ?? null,
        pinned: false,
      });
      toast.success("Posse solta: volta a seguir quem de fato usa a máquina.");
      onOwnerChanged?.();
    } catch {
      toast.error("Não foi possível soltar a posse.");
    } finally {
      setAssigning(null);
    }
  };

  if (loading) return null;
  if (agents.length === 0) return null;

  return (
    <div className="flex flex-col sm:col-span-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Quem usou esta máquina
        </span>
        {ownerPinned && (
          <button
            type="button"
            onClick={unpinOwner}
            disabled={assigning === -1}
            title="Deixar a posse seguir o uso real da máquina de novo"
            className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-white active:scale-[.98] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            {assigning === -1 ? "Soltando…" : "Soltar posse fixada"}
          </button>
        )}
      </div>
      <div className="mt-1.5 flex flex-col gap-2">
        {agents.map((a) => {
          const revogado = !!a.revokedAt;
          return (
            <div
              key={a.id}
              className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
                revogado
                  ? "border-gray-100 bg-gray-50 dark:border-white/10 dark:bg-navy-900/40"
                  : "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-900/20"
              }`}
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-navy-700 dark:text-white">
                  <FiUser className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {a.employeeName || a.userUpn || a.osUser}
                  {!a.employeeId && (
                    <span
                      title="O e-mail reportado não casou com nenhum colaborador do cadastro"
                      className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    >
                      não identificado
                    </span>
                  )}
                </p>
                <p className="truncate text-xs tabular-nums text-gray-700 dark:text-gray-300">
                  perfil {a.osUser} · desde {new Date(a.enrolledAt).toLocaleDateString("pt-BR")}
                  {a.lastSeenAt ? ` · visto ${lastSeenLabel(a.lastSeenAt)}` : " · nunca reportou"}
                  {a.widgetVersion ? ` · widget ${a.widgetVersion}` : ""}
                </p>
                {revogado && (
                  <p className="truncate text-xs italic text-gray-600 dark:text-gray-400">
                    revogado em {new Date(a.revokedAt!).toLocaleDateString("pt-BR")}
                    {a.revokedReason ? ` — ${a.revokedReason}` : ""}
                  </p>
                )}
              </div>

              {!revogado && a.employeeId && a.employeeId !== ownerEmployeeId && (
                <button
                  type="button"
                  onClick={() => assignOwner(a)}
                  disabled={assigning === a.id}
                  title="Fixar este colaborador como dono do equipamento"
                  className="flex shrink-0 items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-white active:scale-[.98] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
                >
                  {assigning === a.id ? "Definindo…" : "Definir como dono"}
                </button>
              )}

              {!revogado && (
                <button
                  type="button"
                  onClick={() => revoke(a)}
                  disabled={revoking === a.id}
                  title="Revogar a credencial desta instalação"
                  className="flex shrink-0 items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-white active:scale-[.98] disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
                >
                  <FiSlash className="h-3.5 w-3.5" aria-hidden="true" />
                  {revoking === a.id ? "Revogando…" : "Revogar"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FleetAgentsPanel;
